import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businessdevelopmentauthority.net';

const AIImplementationPage: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/ai-implementation`,
        'url': `${SITE_URL}/ai-implementation`,
        'name': 'AI Implementation Services - Business Development Authority',
        'description': 'Turn AI strategies into reality with expert implementation services — from architecture design and development to deployment, integration, and ongoing optimization.',
        'isPartOf': { '@id': SITE_URL },
      },
      {
        '@type': 'Service',
        'name': 'AI Implementation',
        'url': `${SITE_URL}/ai-implementation`,
        'description': 'Complete AI implementation lifecycle management: custom development, system integration, deployment, chatbot implementation, analytics integration, and continuous optimization.',
        'serviceType': 'AI Implementation Services',
        'provider': {
          '@type': 'Organization',
          'name': 'Business Development Authority',
          'url': SITE_URL,
        },
        'areaServed': 'US',
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'AI Implementation Services',
          'itemListElement': [
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Custom AI Development' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'System Integration' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Deployment & Scaling' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Chatbot Implementation' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Analytics Integration' } },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/38dca2cca589f677baf42583893f4863.jpg')` }}>
          <div className="max-w-7xl mx-auto px-6 text-center text-white w-full">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">AI Implementation</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">Turn AI strategies into reality with expert implementation services that deliver production-ready solutions</p>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Implement AI Solutions
            </button>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">From Concept to Production-Ready AI</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Our AI Implementation services bridge the gap between strategy and execution. We handle the complete implementation lifecycle—from architecture design and development to deployment, integration, and ongoing optimization.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  Whether you're implementing chatbots, predictive models, computer vision systems, or custom AI applications, we ensure seamless integration with your existing infrastructure and deliver solutions that scale.
                </p>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Software%20engineers%20implementing%20AI%20systems%20with%20code%20on%20multiple%20monitors%2C%20modern%20development%20environment%20with%20AI%20deployment%20dashboards%2C%20professional%20tech%20workspace%20showing%20system%20integration&width=600&height=400&seq=ai-implementation-overview&orientation=landscape" alt="AI Implementation" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Implementation Services Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Implementation Services</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-code-box-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Custom AI Development</h3>
                <p className="text-gray-700">Build tailored AI solutions from scratch using cutting-edge frameworks and best practices for your specific use cases.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-plug-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">System Integration</h3>
                <p className="text-gray-700">Seamlessly integrate AI capabilities with existing systems, databases, and business applications.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-rocket-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Deployment & Scaling</h3>
                <p className="text-gray-700">Deploy AI models to production environments with robust infrastructure that scales with your needs.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-robot-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Chatbot Implementation</h3>
                <p className="text-gray-700">Deploy intelligent conversational AI for customer service, sales, and internal support automation.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-bar-chart-box-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Analytics Integration</h3>
                <p className="text-gray-700">Implement AI-powered analytics and reporting systems for real-time insights and decision support.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-refresh-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Continuous Optimization</h3>
                <p className="text-gray-700">Monitor, maintain, and continuously improve AI systems for optimal performance and accuracy.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Implementation Process Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Our Implementation Process</h2>
            <div className="grid md:grid-cols-5 gap-6">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Requirements Analysis</h3>
                <p className="text-gray-700">Define technical requirements and success criteria</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Architecture Design</h3>
                <p className="text-gray-700">Design scalable and secure AI system architecture</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Development & Testing</h3>
                <p className="text-gray-700">Build and rigorously test AI components</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">4</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Deployment</h3>
                <p className="text-gray-700">Deploy to production with minimal disruption</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">5</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Support & Optimization</h3>
                <p className="text-gray-700">Ongoing monitoring and performance tuning</p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Implementation Excellence</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Enterprise-Grade Security</h3>
                  <p className="text-gray-700">Implement AI with robust security measures, data encryption, and compliance controls.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-speed-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">High Performance</h3>
                  <p className="text-gray-700">Optimize AI systems for speed, efficiency, and scalability under production loads.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-links-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Seamless Integration</h3>
                  <p className="text-gray-700">Connect AI solutions with existing tools, databases, and business processes.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-book-open-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Documentation & Training</h3>
                  <p className="text-gray-700">Comprehensive documentation and team training for successful AI adoption.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Implement AI?</h2>
            <p className="text-xl mb-8">Let's turn your AI vision into production-ready solutions that deliver real business value.</p>
            <button onClick={() => window.REACT_APP_NAVIGATE('/calendar')} className="whitespace-nowrap bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Start Implementation
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AIImplementationPage;
